/**
 * Printable analytics report.
 */
import React, { useState } from "react";
import jsPDF from 'jspdf';
import html2canvas from "html2canvas";
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { VButton, VText } from 'form';
import { BTable } from 'components';
import { useAsterController } from 'context';
import { getUserName } from 'utils/string';
import asterAxios from 'utils/api';
import { Q_ADMIN_USER_REPORT_GET } from 'context/graphql';
import { getFormatDate, getShortTime } from 'utils/date';
import { safeParse } from 'utils/safeData';

const TABLE_ROWS = 25;
const MX = 30, MY = 40;

/**
 * Paginated PDF-ready report of course progress and grades.
 */
const AnalysisReport = () => {

  const { t, i18n } = useTranslation('common');

  const [controller, dispatch] = useAsterController();
  const { user, structure } = controller;

  const [visible, setVisible] = useState(false);

  const [totalRenderItem, setTotalRenderItem] = useState(0);
  const [currRenderItem, setCurrRenderItem] = useState(0);
  const [moduleCount, setModuleCount] = useState(0);

  const [moduleArr, setModuleArr] = useState([]);
  const [logArr, setLogArr] = useState([]);

  const getImageFromHtml = async (pdf, html, width) => {
    const data = await html2canvas(html);
    const img = data.toDataURL('image/png');
    const imgProperties = pdf.getImageProperties(img);
    const imgHeight = (imgProperties.height * width) / imgProperties.width;

    return { img: img, offsetY: imgHeight };
  };

  const generatePDF = async () => {
    setVisible(true);
    asterAxios(dispatch, Q_ADMIN_USER_REPORT_GET, { user_id: user.id })
      .then(res => {
        res = safeParse(res);
        let totalCount = + 3;
        if (res.modules?.data?.length) {
          let pointer = 0, arr = [];
          while (pointer < res.modules?.data?.length) {
            let tmp = pointer;
            arr.push({
              key: pointer,
              data: res.modules.data.slice(tmp, tmp + TABLE_ROWS).map((item, idx) => ({ ...item, index: tmp + idx + 1 }))
            });
            pointer += TABLE_ROWS;
          }
          setModuleArr(arr);
          totalCount += arr.length;
          setModuleCount(arr.length);
        }

        if (res.logs?.data?.length) {
          let pointer = 0, arr = [];
          while (pointer < res.logs?.data?.length) {
            let tmp = pointer;
            arr.push({
              key: pointer,
              data: res.logs.data.slice(tmp, tmp + TABLE_ROWS).map((item, idx) => ({ ...item, index: tmp + idx + 1 }))
            });
            pointer += TABLE_ROWS;
          }
          setLogArr(arr);
          totalCount += arr.length;
        }

        setTotalRenderItem(totalCount);

        setTimeout(async () => {
          const pdf = new jsPDF({ orientation: "portrait", unit: 'px', format: 'a4', putOnlyUsedFonts: true });
          const width = pdf.internal.pageSize.getWidth() - MX * 2;
          const height = pdf.internal.pageSize.getHeight() - MY * 2;

          let x = MX, y = MY;

          for (let i = 0; i < totalCount; i++) {
            const html = document.querySelector(`#v-report-${i}`);
            if (!html) continue;

            const imgData = await getImageFromHtml(pdf, html, width);
            if (!imgData || !imgData.img) continue;

            if(currRenderItem === totalCount) break;
            setCurrRenderItem(page => page + 1);
            if (y !== MY && (y + imgData.offsetY > height + MY)) {
              y = MY;
              pdf.addPage('a4', 'portrait');
            }

            pdf.addImage(imgData.img, 'PNG', x, y, width, imgData.offsetY);
            y += imgData.offsetY + 5;
          }
          pdf.save('report.pdf');

          setCurrRenderItem(0);
          setVisible(false);
        }, 200);
      }).catch((err) => {
        if (process.env.NODE_ENV !== "production") console.log(err);
        setVisible(false);
      });
  };

  const moduleColumns = [
    {
      name: <VText>No</VText>,
      cell: rw => <VText>{rw.index}</VText>,
      width: '70px',
    },
    {
      name: <VText>{t('Date')}</VText>,
      cell: rw => <VText>{getFormatDate(rw.date, i18n.language)}</VText>,
      minWidth: '130px',
    },
    {
      name: <VText>{t('Course Title')}</VText>,
      cell: rw => <VText>{rw.course_title}</VText>,
      minWidth: '250px',
    },
    {
      name: <VText>{t('Module Title')}</VText>,
      cell: rw => <VText>{rw.details.module_title}</VText>,
      minWidth: '250px',
    },
    {
      name: <VText>{t('Percent')}</VText>,
      cell: rw => <VText>{rw.details.total_count ? Math.round(rw.details.correct_count * 100 / rw.details.total_count) : 0} %</VText>,
      minWidth: '70px',
    },
    {
      name: <VText>{t('Quiz Spent')}</VText>,
      cell: rw => <VText>{Math.round(rw.details.quiz_spent / 60)} min</VText>,
      minWidth: '90px',
    },
    {
      name: <VText>{t('Total Spent')}</VText>,
      cell: rw => <VText>{Math.round(rw.details.total_spent / 60)} min</VText>,
      minWidth: '90px',
    },
  ];

  const logColumns = [
    {
      name: <VText>No</VText>,
      cell: rw => <VText>{rw.index}</VText>,
      width: '70px',
    },
    {
      name: <VText>{t('Date')}</VText>,
      selector: row => row.date,
      cell: rw => <VText>{getFormatDate(rw.date)} - {getShortTime(rw.date)}</VText>,
      sortable: true
    },
    {
      name: <VText>{t('Device')}</VText>,
      selector: row => row.device,
      cell: rw => <VText>{rw.device}</VText>,
      sortable: true
    },
    {
      name: <VText>{t('IP Address')}</VText>,
      selector: row => row.ip_address,
      cell: rw => <VText>{rw.ip_address}</VText>,
      sortable: true
    },
    {
      name: <VText>{t('Browser')}</VText>,
      selector: row => row.browser,
      cell: rw => <VText>{rw.browser}</VText>,
      sortable: true
    }
  ];

  return <>

    <div className="ml-4 flex flex-col items-center">
      <VButton variant="contained" color="primary" className="h-min" onClick={generatePDF} loading={visible}>
        <Icon className="text-xl" icon="humbleicons:download-alt m-2" />&nbsp;{t("Download report")}
      </VButton>
      {visible && <VText className="mt-1 text-xs" div>{t("Processing...")} {currRenderItem + 1} / {totalRenderItem || 1}</VText>}
    </div>

    {
      visible && <div className="w-full">
        <div className='v-report-table'>

          <div id="v-report-0" className="v-report-item">
            <div className="w-full text-center">
              <VText className="text-5xl" div>{getUserName(user)}</VText>&nbsp;
              <VText className="text-lg" div>({user.email})</VText>
              <VText className="md:text-4xl text-xl my-4" div>{structure.course_title}</VText>
            </div>
          </div>

          {
            moduleArr.map((item, idx) => <div key={item.key} id={`v-report-${idx + 3}`} className="v-report-item">
              {idx === 0 && <VText className="text-2xl mb-4" div>{t("Progross by module")}</VText>}
              <BTable
                className="w-full v-report-table"
                columns={moduleColumns}
                data={item.data}
              />
            </div>)
          }

          {
            logArr.map((item, idx) => <div key={item.key} id={`v-report-${idx + moduleCount + 4}`} className="v-report-item">
              {idx === 0 && <VText className="text-2xl mb-4" div>{t("Logs history")}</VText>}
              <BTable
                className="w-full v-report-table"
                columns={logColumns}
                data={item.data}
              />
            </div>)
          }

        </div>
      </div>
    }
  </>;
};

export default AnalysisReport;